import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { FinancialProduct } from '@interfaces/financialProduct';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useAccordionTokens } from '@ovComponents/2-component/accordion/accordian.tokens';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { PortfolioSelectionType } from '@ovComponents/4-module/workflowCompletion/subSteps/reviewPortfolioChanges/portfolioSelectionType';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { calculatePrimaryAssetClassHoldings } from '../holdings.resources';
import { AccordionHoldings } from './accordionHoldings';

const generateRiskLevel = (riskRetunLevel?: number) => {
  if (riskRetunLevel && riskRetunLevel > 0 && riskRetunLevel < 21) {
    return 'VERY_LOW';
  }
  if (riskRetunLevel && riskRetunLevel > 20 && riskRetunLevel < 41) {
    return 'LOW';
  }
  if (riskRetunLevel && riskRetunLevel > 40 && riskRetunLevel < 61) {
    return 'MEDIUM';
  }
  if (riskRetunLevel && riskRetunLevel > 60 && riskRetunLevel < 81) {
    return 'HIGH';
  }
  if (riskRetunLevel && riskRetunLevel > 80 && riskRetunLevel < 101) {
    return 'VERY_HIGH';
  }
  return null;
};
const AVAILABLE_THEMES = `
  availableThemes {
    id
    key
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
  }
`;
const SUGGESTED_PRODUCT = `
  id
  translatedName { ${TRANSLATED_STRING_FIELDS} }
  riskReturnLevel
  theme {
    id
    key
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
  }
  primaryAssetClass {
    id
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    key
  }
  secondaryAssetClass {
    id
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    key
  }
  translatedName { ${TRANSLATED_STRING_FIELDS} }
  translatedUrl { ${TRANSLATED_STRING_FIELDS} }
  children {
    id
    percentage
    taxRank
    financialProduct {
      id
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      riskReturnLevel
      primaryAssetClass {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
        key
      }
      theme {
        id
        key
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
      }
      secondaryAssetClass {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
        key
      }
    }
  }
`;
const FINANCIAL_PRODUCT = `
financialProduct {
  id
  translatedName { ${TRANSLATED_STRING_FIELDS} }
  riskReturnLevel
  theme {
    id
    key
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
  }
  primaryAssetClass {
    id
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    key
  }
  secondaryAssetClass {
    id
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    key
  }
  children {
    id
    percentage
    taxRank
    financialProduct {
      id
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      riskReturnLevel
      primaryAssetClass {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
        key
      }
      theme {
        id
        key
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
      }
      secondaryAssetClass {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
        key
      }
    }
  }
}
`;
export const FETCH_FINANCIAL_PRODUCT = gql`
  query fetchFinancialProduct($input: FetchFinancialProductInput!) {
    fetchFinancialProduct(input: $input) {
      ${FINANCIAL_PRODUCT}
    }
  }
`;

const FETCH_GOAL = gql`
  query fetchGoal($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        name
        type
        state
        ${AVAILABLE_THEMES}
        ${FINANCIAL_PRODUCT}
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
        subAccounts {
          id
          account {
            type
          }
          ${AVAILABLE_THEMES}
          ${FINANCIAL_PRODUCT}
          suggestedFinancialProduct {
            ${SUGGESTED_PRODUCT}
          }
        }
      }
    }
  }
`;

type HoldingCardProps = {
  financialProduct?: FinancialProduct;
  type: PortfolioSelectionType;
  onEdit?: (selectedId: string) => any;
  goalId?: string;
  level: 'GOAL' | 'SUB_ACCOUNT';
  subAccountId?: string;
  options: any;
  isSelected?: boolean;
  onSelect?: () => void;
};

const HoldingCard = ({
  financialProduct,
  type,
  goalId,
  level,
  subAccountId,
  options,
  isSelected,
  onSelect,
}: HoldingCardProps) => {
  const { t } = useTranslation(['client']);
  const { sys } = useThemeTokens();
  const tokens = useThemeTokens(useAccordionTokens());
  const { comp } = tokens;
  const [expanded, setExpanded] = useState(false);
  const [availableThemVal, setAvailableThemeVal] = useState<any>();
  const [holdings, setHoldings] = useState<any[]>();

  // Determine if card is selectable - CURRENT cards are only selectable if suggestable is true
  const isSelectable =
    type === PortfolioSelectionType.SUGGESTED ||
    (type === PortfolioSelectionType.CURRENT && !financialProduct?.suggestable);

  const { data: goalData } = useQuery(FETCH_GOAL, {
    variables: { goalId },
    fetchPolicy: 'no-cache',
    skip: !goalId || !expanded,
  });

  const handleClick = (e: React.MouseEvent) => {
    if (isSelectable && onSelect) {
      e.stopPropagation();
      onSelect();
    }
  };

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (level === 'GOAL') {
      if (type === PortfolioSelectionType.CURRENT && goalData) {
        setAvailableThemeVal(goalData.fetchGoal.goal.availableThemes);
        if (goalData.fetchGoal.goal?.financialProduct?.children) {
          const h = calculatePrimaryAssetClassHoldings(
            goalData.fetchGoal.goal?.financialProduct?.children.map(
              (x: any) => ({ ...x, totalCents: x.percentage }),
            ),
            100,
          );
          setHoldings(h ?? []);
        }
      }
      if (type === PortfolioSelectionType.SUGGESTED && goalData) {
        setAvailableThemeVal(goalData.fetchGoal.goal.availableThemes);
        if (goalData.fetchGoal.goal?.suggestedFinancialProduct?.children) {
          const h = calculatePrimaryAssetClassHoldings(
            goalData.fetchGoal.goal?.suggestedFinancialProduct?.children.map(
              (x: any) => ({ ...x, totalCents: x.percentage }),
            ),
            100,
          );
          setHoldings(h ?? []);
        }
      }
    }
    if (level === 'SUB_ACCOUNT' && subAccountId) {
      const relatedSubAccount = goalData?.fetchGoal.goal?.subAccounts?.find(
        (a: SubAccount) => a.id === subAccountId,
      );
      if (
        type === PortfolioSelectionType.CURRENT &&
        goalData &&
        relatedSubAccount
      ) {
        setAvailableThemeVal(relatedSubAccount?.availableThemes);
        if (relatedSubAccount?.financialProduct?.children) {
          const h = calculatePrimaryAssetClassHoldings(
            relatedSubAccount?.financialProduct?.children.map((x: any) => ({
              ...x,
              totalCents: x.percentage,
            })),
            100,
          );
          setHoldings(h ?? []);
        }
      }
      if (
        type === PortfolioSelectionType.SUGGESTED &&
        goalData &&
        relatedSubAccount
      ) {
        setAvailableThemeVal(relatedSubAccount.availableThemes);
        if (relatedSubAccount?.suggestedFinancialProduct?.children) {
          const h = calculatePrimaryAssetClassHoldings(
            relatedSubAccount?.suggestedFinancialProduct?.children.map(
              (x: any) => ({ ...x, totalCents: x.percentage }),
            ),
            100,
          );
          setHoldings(h ?? []);
        }
      }
    }
  }, [goalData, type, level, subAccountId]);

  const showUrlModelPortfolioUrl =
    options?.displayModelPortfolioUrl &&
    translateBackend(financialProduct?.translatedUrl) ? (
      <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
        {`[${t('components:portfolioFundFacts')}](${translateBackend(financialProduct?.translatedUrl)})`}
      </ReactMarkdown>
    ) : (
      <></>
    );

  return (
    <Accordion
      expanded={expanded}
      variant='outlined'
      disableGutters
      onChange={handleAccordionChange}
      onClick={handleClick}
      sx={{
        cursor: isSelectable ? 'pointer' : 'default',
        '&:before': {
          display: 'none',
          border: 'none',
        },
        '&.MuiAccordion-root': {
          border: `1px solid ${isSelected ? sys.color.positive : comp.accordion.multipleBorderColor}`,
        },
        '&:last-of-type': {
          borderRadius: `${comp.accordion.borderRadius} !important`,
          '.MuiCollapse-root': {
            borderRadius: `${comp.accordion.borderRadius} !important`,
          },
        },
        '&:last-of-type&:first-of-type': {
          borderRadius: `${comp.accordion.borderRadius} !important`,
          '.MuiCollapse-root': {
            borderRadius: `${comp.accordion.borderRadius} !important`,
          },
        },
        '&:first-of-type': {
          borderRadius: `${comp.accordion.borderRadius} !important`,
          '.MuiCollapse-root': {
            borderRadius: `${comp.accordion.borderRadius} !important`,
          },
        },
        '.MuiAccordionSummary-root': { borderTop: 'none !important' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
        sx={{
          '.MuiAccordionSummary-content': {
            justifyContent: 'space-between !important',
          },
          '.MuiAccordionSummary-expandIconWrapper': {
            position: 'absolute',
            top: '10px',
            right: '15px',
          },
          '.MuiAccordionSummary-root': { borderTop: 'none' },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box display='flex' justifyContent='space-between'>
            <Typography
              variant='titleMedium'
              sx={{
                color: isSelectable ? 'inherit' : 'text.disabled',
              }}
            >
              {t(
                `${type === PortfolioSelectionType.CURRENT ? 'currentPortfolio' : 'recommendedPortfolio'}`,
              )}
              :
              <span style={{ marginLeft: '5px', marginRight: '5px' }}>
                {financialProduct?.riskReturnLevel
                  ? t(
                      `riskReturnLevels.${generateRiskLevel(financialProduct?.riskReturnLevel)}`,
                    )
                  : ''}
              </span>
              - {translateBackend(financialProduct?.theme?.translatedName)}
            </Typography>
          </Box>
          <Box display='block' justifyContent='space-between' sx={{ mt: 1 }}>
            <Typography variant='bodySmall'>
              {translateBackend(financialProduct?.theme?.translatedDescription)}
            </Typography>
            {showUrlModelPortfolioUrl}
          </Box>
        </Box>
      </AccordionSummary>
      {goalData && (
        <AccordionDetails>
          <hr
            style={{
              borderTop: `1px solid ${comp.accordion.multipleBorderColor}`,
            }}
          ></hr>
          <Box display='flex' justifyContent='space-between'>
            <Typography variant='titleMedium'>
              {t('portfolioBreakdown')}
            </Typography>
          </Box>
          <br></br>
          <Typography variant='bodySmall' sx={{ ml: 1 }}>
            {translateBackend(availableThemVal?.translatedDescription)}
          </Typography>
          <AccordionHoldings
            assetClassHoldings={holdings ?? []}
            loading={false}
          />
        </AccordionDetails>
      )}
    </Accordion>
  );
};
export default HoldingCard;
